import {injectable} from 'inversify';
import {FirebaseApp, initializeApp} from 'firebase/app';
import {
    Auth,
    browserLocalPersistence,
    browserSessionPersistence,
    getAuth,
    NextOrObserver,
    setPersistence,
    signInWithEmailAndPassword,
    User,
    UserCredential,
    updatePhoneNumber,
    PhoneAuthProvider,
    RecaptchaVerifier,
    sendSignInLinkToEmail,
    isSignInWithEmailLink,
    signInWithEmailLink,
    sendEmailVerification,
    sendPasswordResetEmail,
    signOut,
} from 'firebase/auth';
import {createUserWithEmailAndPassword, PhoneAuthCredential} from "@firebase/auth";

const firebaseConfig = {
    apiKey: 'AIzaSyAkAITe0THhYDJoRjsmA9F8VNQ8ZEqQPDA',
};

@injectable()
export class FirebaseService {
    private firebaseApp: FirebaseApp;
    private firebaseAuth: Auth;

    constructor() {
        this.firebaseApp = initializeApp(firebaseConfig);
        this.firebaseAuth = getAuth(this.firebaseApp);
    }

    public signInWithEmailAndPassword(email: string, password: string, keepLoggedIn: boolean = false): Promise<UserCredential> {
        const loggedInStrategy = keepLoggedIn ? browserLocalPersistence : browserSessionPersistence;
        return setPersistence(this.firebaseAuth, loggedInStrategy).then(() => signInWithEmailAndPassword(this.firebaseAuth, email, password));
    }

    public createUser(email: string, password: string): Promise<UserCredential> {
        return createUserWithEmailAndPassword(this.firebaseAuth, email, password);
    }

    public getPhoneVerificationId(containerId: string, user: User, phoneNumber: string): Promise<string> {
        const appVerifier = new RecaptchaVerifier(containerId, {size: 'invisible'}, this.firebaseAuth);
        const authProvider = new PhoneAuthProvider(this.firebaseAuth);
        return authProvider.verifyPhoneNumber(phoneNumber, appVerifier);
    }

    public updatePhoneNumber(user: User, verificationId: string, verificationCode: string): Promise<void> {
        const phoneCredential = PhoneAuthProvider.credential(verificationId, verificationCode)
        return updatePhoneNumber(user, phoneCredential);
    }

    public sendSignInLinkToEmail(email: string): Promise<void> {
        return sendSignInLinkToEmail(this.firebaseAuth, email, {
            handleCodeInApp: true,
            url: `${location.protocol}//${location.host}`
        })
    }

    public sendEmailVerification(user: User, actionUrl: string): Promise<void> {
        return sendEmailVerification(user, {url: actionUrl});
    }

    public isSignInWithEmailLink(): boolean {
        return isSignInWithEmailLink(this.firebaseAuth, location.href);
    }

    public signInWithEmailLink(email: string): Promise<UserCredential> {
        return signInWithEmailLink(this.firebaseAuth, email, location.href);
    }

    public sendPasswordResetEmail(email: string, actionUrl: string): Promise<void> {
        return sendPasswordResetEmail(this.firebaseAuth, email, {url: actionUrl});
    }

    public getCurrentUser(): User {
        return this.firebaseAuth.currentUser;
    }

    public getUserIdToken(): Promise<string> {
        return this.getCurrentUser().getIdToken(false);
    }

    public onAuthStateChanged(nextOrObserver: NextOrObserver<User | null>): void {
        this.firebaseAuth.onAuthStateChanged(nextOrObserver);
    }

    public signOut(): Promise<void> {
        return signOut(this.firebaseAuth);
    }
}
